export interface MetaDataType {
  id: string,
  '$ref': string,
  name: string,
  key: string,
  type: 'Number' | 'Date' | 'Text' | 'Hyperlink',
  encrypted?: boolean,
  exportable?: boolean,
  readOnly?: boolean,
  dataExplorerKey: null,
  acdDataTypeId?: number,
  description?: null,
  reconColumn?: null,
}

export interface MetadataFieldsResponse extends Array<MetaDataType> { }

export interface Column {
  id: string,
  name: string,
  label: string,
  isEnabled: boolean,
  key: string,
  order: number,
  width: number,
  isSelected: boolean,
}

export interface CategoryColumns {
  category: string,
  columns: Column[],
}

export interface AllColumnsResponse extends Array<CategoryColumns> { }

export interface UserPrefsColumn extends Column {
  category: string,
  label: string,
  disablePadding: boolean,
}

export interface UserPrefsCategoryColumns {
  category: string,
  columns: UserPrefsColumn[],
}


export interface UserPrefsColumnsResponse {
  columnData: UserPrefsCategoryColumns[],
}

export const GOAL_COLUMN_CATEGORY = {
  ORGANIZATION: 'organization_fields',
  EVALUATION: 'evaluation_fields',
  DATE: 'date_fields',
  CONTACT: 'contact_fields',
  ANALYSIS: 'goal_fields',
  METADATA: 'metadata_fields',
}
export const ACTION_COLUMN = {
  contact_goal_action_evaluate: 'evalgoalactionevaluate',
  contact_goal_action_calibrate: 'evalgoalactioncalibrate',
  contact_goal_action_review: 'evalgoalactionreview',
}

export type ActionColumnKey = keyof typeof ACTION_COLUMN;

export interface Evaluation {
  $ref: string,
  id: number,
  stateId: number,
  totalScore: number,
  evaluated: number,
  evaluatedTz: string,
  responseDate: number | null,
  responseState: string | null,
}

export interface EvalFormBandRange {
  band: number,
  begin: number,
  end: number,
}

export interface EvalForm {
  evalFormId: number,
  $ref: string,
  name: string,
  bandRanges: EvalFormBandRange[],
}

export interface Contact {
  id: number,
  startTime: number,
  tz: string,
  tzOffset: number,
  callDuration: number,
  ani: string,
  dnis: string,
  line: string,
  audioUploadState: number,
  screenUploadState: number,
  recordingType: string,
  icmCallId: string | null,
  assocCallId: string | null,
  contactType: string,
  isCalibration: boolean,
  fromAddress: string | null,
  toAddress: string | null,
  subject: string | null,
  dailyWorkflowRule: string | null,
  endOfCallWorkflowRule: string | null,
  retentionPolicyName: string | null,
  surveyScore: number | null,
  surveyName: string | null,
  isRootRecording: boolean,
  evalStateId: number,
  videoFileUploadStateId: number | null,
  audioFileUploadStateId: number | null,
  isInbound: boolean,
  audioDisplayStateId: number,
  screenDisplayStateId: number,
  agent: {
    agentId: number,
    $ref: string,
    displayId: string,
    agentAcdId: string | null,
    lastName: string,
    firstName: string,
    username: string,
  },
  team: {
    $ref: string,
    name: string,
  },
  group: {
    $ref: string,
    name: string,
    id: number,
  },
  training: boolean,
  hr: boolean,
  reason: {
    reasonId: number,
    key: string,
  },
  evalScorePrediction: number | null,
  netPromoterScore: number | null,
  netPromoterScorePrediction: number | null,
  sentiment: number | null,
  evalForm: EvalForm | null,
  evaluator: {
    $ref: string,
    evaluatorId: number,
    firstName: string,
    lastName: string,
    displayId: string,
    username: string,
  },
  approver: {
    $ref: string,
    approverId: number,
    firstName: string | null,
    lastName: string | null,
    displayId: string,
    username: string | null,
  },
  evaluation: Evaluation | null,
  goal: {
    action: string,
    dueDate: number,
    name: string,
    id: number,
    instruction: string,
    percentToComplete: number,
  } | null,
  events: {
    $ref: string,
  },
  eventCalculations: {
    talkover: {
      totalDuration: number,
      maxDuration: number | null,
      minDuration: number | null,
      avgDuration: number | null,
      totalEvents: number,
      percentOfCall: number,
    },
    silence: {
      totalDuration: number,
      maxDuration: number | null,
      minDuration: number | null,
      avgDuration: number | null,
      totalEvents: number,
      percentOfCall: number,
    },
    pause: {
      totalDuration: number,
      maxDuration: number | null,
      minDuration: number | null,
      avgDuration: number | null,
      totalEvents: number,
      percentOfCall: number,
    },
    hold: {
      totalDuration: number,
      maxDuration: number | null,
      minDuration: number | null,
      avgDuration: number | null,
      totalEvents: number,
      percentOfCall: number,
    },
  },
  metadata: Record<string, {
    metadata: string,
    name: string,
    value: string | number | null,
    type: string,
    encrypted: boolean,
    exportable: boolean,
    readOnly: boolean,
  }>,
  redacted: boolean,
  channel: string,
}

export const ACTIVE_GOALS_COLUMN_I18N_MAPPING: { [key: string]: string } = {
  goalId: 'goalIdLabelv2',
  name: 'goalName',
  goalStatus: 'status',
  dueDate: 'dueDateSentenceCase',
  goalActionName: 'activity',
  totalContacts: 'totalNumberOfContacts',
  contactsRemaining: 'contactsRemaining',
}

export interface GoalProgress {
  goalId: number,
  name: string,
  totalProgress: number,
  totalForComplete: number,
  contactsCompleted: number,
  goalActionId: number,
  dueDate: string,
  progressDetails: Record<string, unknown>[],
}

export interface UserGoalProgress {
  totalProgress: number,
  totalForComplete: number,
  contactsCompleted: number,
  personId: number,
  fName: string,
  lName: string,
  progress: GoalProgress[],
  progressDetails: Record<string, unknown>[],
}

export interface SearchStats {
  avgCallDuration: number,
  avgScore: number | null,
  count: number,
}

export interface ContactGoalProgressResponse extends Array<UserGoalProgress> {}
